import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { PRODUCT_BY_CODE } from "../data/products";
import { toast } from "sonner";
import { Anchor, LogOut, RefreshCw } from "lucide-react";

const STATUS_FILTERS = [
  { key: "all", value: null },
  { key: "new", value: "new" },
  { key: "in_review", value: "in_review" },
  { key: "archived", value: "archived" },
];

function StatusPill({ status }) {
  const styles = {
    new: "bg-secondary text-secondary-foreground",
    in_review: "bg-accent text-accent-foreground border border-border",
    archived: "bg-muted text-muted-foreground",
  };
  return (
    <span
      className={`text-[10px] tracking-eyebrow uppercase px-2 py-1 ${styles[status] || styles.archived}`}
    >
      {status.replace("_", " ")}
    </span>
  );
}

export default function AdminDashboardPage() {
  const { user, token, logout, loading: authLoading } = useAuth();
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, in_review: 0, archived: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!authLoading && !token) {
      navigate("/admin/login", { replace: true });
    }
  }, [authLoading, token, navigate]);

  const headers = { Authorization: `Bearer ${token}` };

  const refresh = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const filterVal = STATUS_FILTERS.find((f) => f.key === filter)?.value;
      const [quotesRes, statsRes] = await Promise.all([
        api.get("/admin/quotes", {
          headers,
          params: filterVal ? { status: filterVal } : {},
        }),
        api.get("/admin/stats", { headers }),
      ]);
      setQuotes(quotesRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error("Error", {
        description:
          formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not load quotes",
      });
      if (err.response?.status === 401) {
        logout();
        navigate("/admin/login", { replace: true });
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, filter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/quotes/${id}`, { status }, { headers });
      toast.success("Status updated");
      refresh();
    } catch (err) {
      toast.error("Error", {
        description:
          formatApiErrorDetail(err.response?.data?.detail) || "Update failed",
      });
    }
  };

  const formatDate = (iso) =>
    new Date(iso).toLocaleString(lang, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Loading...
      </div>
    );
  }

  return (
    <div
      data-testid="admin-dashboard"
      className="min-h-screen bg-background"
    >
      <header className="border-b border-border bg-background sticky top-0 z-30">
        <div className="px-6 md:px-12 lg:px-20 h-20 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2">
            <Anchor strokeWidth={1.4} className="h-5 w-5 text-primary" />
            <span className="font-display text-lg md:text-xl text-primary">
              World Commodity Supply
            </span>
            <span className="hidden md:inline text-xs tracking-eyebrow uppercase text-muted-foreground ml-3 border-l border-border pl-3">
              {t.admin.dashboard}
            </span>
          </Link>
          <div className="flex items-center gap-4">
            {user && (
              <span className="hidden md:inline text-sm text-muted-foreground">
                {user.email}
              </span>
            )}
            <button
              data-testid="admin-refresh"
              onClick={refresh}
              className="p-2 border border-border hover:bg-accent transition-colors"
              aria-label="refresh"
            >
              <RefreshCw size={16} />
            </button>
            <button
              data-testid="admin-logout"
              onClick={() => {
                logout();
                navigate("/admin/login", { replace: true });
              }}
              className="inline-flex items-center gap-2 text-sm px-4 py-2 border border-border hover:bg-accent transition-colors"
            >
              <LogOut size={14} /> {t.admin.logout}
            </button>
          </div>
        </div>
      </header>

      <main className="px-6 md:px-12 lg:px-20 py-10 space-y-10">
        <section
          data-testid="admin-stats"
          className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border"
        >
          {[
            { key: "total", label: t.admin.filter_all, value: stats.total },
            { key: "new", label: t.admin.filter_new, value: stats.new },
            { key: "in_review", label: t.admin.filter_review, value: stats.in_review },
            { key: "archived", label: t.admin.filter_archived, value: stats.archived },
          ].map((s) => (
            <div key={s.key} className="bg-card p-6 space-y-2">
              <p className="text-[11px] tracking-eyebrow uppercase text-muted-foreground">
                {s.label}
              </p>
              <p className="font-display text-4xl md:text-5xl text-primary">{s.value}</p>
            </div>
          ))}
        </section>

        <section>
          <div className="flex flex-wrap gap-2 mb-6">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`text-xs tracking-eyebrow uppercase px-4 py-2 border transition-colors ${
                  filter === f.key
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-foreground border-border hover:bg-accent"
                }`}
              >
                {t.admin[`filter_${f.key}`] || f.key}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="text-muted-foreground text-sm">Loading…</p>
          ) : quotes.length === 0 ? (
            <p
              data-testid="admin-no-quotes"
              className="text-muted-foreground text-sm py-8"
            >
              {t.admin.no_quotes}
            </p>
          ) : (
            <div className="space-y-px bg-border">
              <div className="hidden md:grid grid-cols-12 gap-4 bg-muted px-4 py-3 text-[11px] tracking-eyebrow uppercase text-muted-foreground">
                <span className="col-span-3">{t.admin.company}</span>
                <span className="col-span-2">{t.admin.product}</span>
                <span className="col-span-2">{t.admin.quantity}</span>
                <span className="col-span-2">{t.admin.country}</span>
                <span className="col-span-2">{t.admin.received}</span>
                <span className="col-span-1 text-right">{t.admin.status}</span>
              </div>

              {quotes.map((q) => (
                <div
                  key={q.id}
                  data-testid={`quote-row-${q.id}`}
                  className="bg-card"
                >
                  <button
                    onClick={() => setExpanded(expanded === q.id ? null : q.id)}
                    className="w-full grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-4 py-4 text-left hover:bg-accent/40 transition-colors"
                  >
                    <div className="md:col-span-3">
                      <p className="font-display text-lg leading-tight">
                        {q.company}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {q.contact_name} · {q.email}
                      </p>
                    </div>
                    <p className="md:col-span-2 text-sm self-center">
                      {PRODUCT_BY_CODE[q.product]?.titles[lang] || q.product}
                    </p>
                    <p className="md:col-span-2 text-sm self-center">
                      {q.quantity_mt} MT
                    </p>
                    <p className="md:col-span-2 text-sm self-center">
                      {q.country}
                    </p>
                    <p className="md:col-span-2 text-sm self-center text-muted-foreground">
                      {formatDate(q.created_at)}
                    </p>
                    <div className="md:col-span-1 md:justify-self-end self-center">
                      <StatusPill status={q.status} />
                    </div>
                  </button>

                  {expanded === q.id && (
                    <div
                      data-testid={`quote-details-${q.id}`}
                      className="border-t border-border bg-muted/30 px-4 py-6 grid grid-cols-1 md:grid-cols-12 gap-6"
                    >
                      <dl className="md:col-span-8 grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
                        <div>
                          <dt className="text-[10px] uppercase tracking-eyebrow text-muted-foreground">
                            Phone
                          </dt>
                          <dd>{q.phone || "—"}</dd>
                        </div>
                        <div>
                          <dt className="text-[10px] uppercase tracking-eyebrow text-muted-foreground">
                            Destination port
                          </dt>
                          <dd>{q.destination_port || "—"}</dd>
                        </div>
                        <div>
                          <dt className="text-[10px] uppercase tracking-eyebrow text-muted-foreground">
                            Incoterm
                          </dt>
                          <dd>{q.incoterm || "—"}</dd>
                        </div>
                        <div className="col-span-2">
                          <dt className="text-[10px] uppercase tracking-eyebrow text-muted-foreground">
                            Notes
                          </dt>
                          <dd className="whitespace-pre-wrap">
                            {q.message || "—"}
                          </dd>
                        </div>
                      </dl>
                      <div className="md:col-span-4 flex flex-col gap-2">
                        {q.status !== "new" && (
                          <button
                            data-testid={`status-new-${q.id}`}
                            onClick={() => updateStatus(q.id, "new")}
                            className="text-xs tracking-eyebrow uppercase px-4 py-2 border border-border hover:bg-accent transition-colors"
                          >
                            {t.admin.mark_new}
                          </button>
                        )}
                        {q.status !== "in_review" && (
                          <button
                            data-testid={`status-review-${q.id}`}
                            onClick={() => updateStatus(q.id, "in_review")}
                            className="text-xs tracking-eyebrow uppercase px-4 py-2 bg-secondary text-secondary-foreground hover:bg-[#C08B46] transition-colors"
                          >
                            {t.admin.mark_review}
                          </button>
                        )}
                        {q.status !== "archived" && (
                          <button
                            data-testid={`status-archive-${q.id}`}
                            onClick={() => updateStatus(q.id, "archived")}
                            className="text-xs tracking-eyebrow uppercase px-4 py-2 border border-border hover:bg-muted transition-colors"
                          >
                            {t.admin.mark_archived}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
